import userflow, { Attributes, EventAttributes } from 'userflow.js';

import { AnalyticsIdentity, AnalyticsPlugin, Properties } from '../analytics';
import { omitUndefined } from '../omit-undefined';

interface UserflowsConfig {
	userflowToken: string;
}

interface QueuedTrack {
	event: string;
	props?: Properties;
}

const MAX_INIT_ATTEMPTS = 4;
const MAX_QUEUED_TRACKS = 50;

export class Userflows implements AnalyticsPlugin {
	private _initAttempts = 0;
	private hasInitialized = false;
	private hasIdentified = false;
	private isAvailable = true;
	private pendingIdentify: AnalyticsIdentity | null = null;
	private trackQueue: QueuedTrack[] = [];

	constructor(private readonly config: UserflowsConfig) {}

	get name(): string {
		return 'userflows';
	}

	private getCompanyAttributes(props: AnalyticsIdentity): Attributes {
		return omitUndefined({
			name: props.workspaceName,
			isInBetaProgram: props.isInBetaProgram,
			isSandbox: props.isSandbox,
			availableFeatures: props.availableFeatures,
			enabledFeatures: props.enabledFeatures,
			featureFlags: props.featureFlags,
			conversionDate: props.conversionDate,
			creationDate: props.creationDate,
			renewalDate: props.renewalDate,
			// Explicitly cleared, not omitted. Userflow merges, so dropping the key would freeze
			// whatever the last signing-in user stamped here before it moved to the user record.
			// `omitUndefined` keeps null, which is how a vendor is told to clear a field.
			registrationDate: null,
			industry: props.industry,
			tags: props.tags,
			organisationId: props.organisationId,
			itemQuota: props.itemQuota,
			itemQuotaPercentage: props.itemQuotaPercentage,
			subscriptionPlan: props.subscriptionPlan,
			billingPeriod: props.billingPeriod,
			progress: props.progress,
			...(props.featureTrials != null && {
				featureTrials: props.featureTrials.map((trial) =>
					trial.purchased ? `${trial.feature}:${trial.status}:purchased` : `${trial.feature}:${trial.status}`
				),
			}),
		});
	}

	private getUserAttributes(props: AnalyticsIdentity): Attributes {
		return omitUndefined({
			userId: props.userId,
			name: props.name,
			email: props.email,
			// The signing-in user's own account creation date. It used to be written to the
			// company record, where every identify restamped the workspace with whoever signed
			// in last; the workspace's own age is `creationDate`.
			registrationDate: props.registrationDate,
			screenWidth: props.screenWidth,
			screenHeight: props.screenHeight,
		});
	}

	private getUserOptionsForGroup(props: AnalyticsIdentity): Attributes {
		return omitUndefined({
			seatType: props.seatType,
			role: props.userRole,
			userTimezone: props.userTimezone,
			isAccountOwner: props.isAccountOwner,
			isAdministrator: props.isAdministrator,
			isSyncedUser: props.isSyncedUser,
			canLoginWithSSO: props.canLoginWithSSO,
		});
	}

	private flushTrackQueue(): void {
		const queue = this.trackQueue.splice(0);
		for (const { event, props } of queue) {
			try {
				void userflow?.track(event, props ? this.transformPageAttributes(props) : undefined)?.catch((error) => {
					// eslint-disable-next-line no-console
					console.error('Error tracking event', error);
				});
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error tracking event', error);
			}
		}
	}

	private disableAfterLoadFailure(error: unknown): void {
		this.isAvailable = false;
		this.hasInitialized = false;
		this.hasIdentified = false;
		this.pendingIdentify = null;
		this.trackQueue = [];
		// eslint-disable-next-line no-console
		console.warn('Userflow.js could not be loaded; userflow analytics disabled.', error);
	}

	private runGroup(props: AnalyticsIdentity): void {
		// An empty workspace id would write the company attributes onto whichever company this
		// browser last grouped.
		if (!props.workspaceId) return;

		void userflow
			?.group(props.workspaceId, this.getCompanyAttributes(props), {
				membership: this.getUserOptionsForGroup(props),
			})
			?.catch((error) => {
				// eslint-disable-next-line no-console
				console.error('Error grouping user', error);
			});
	}

	private runIdentify(props: AnalyticsIdentity): void {
		if (!this.isAvailable || !userflow || !props.userId) return;

		void userflow
			?.identify(props.userId, this.getUserAttributes(props))
			?.then(() => {
				this.hasIdentified = true;
				// Chained on purpose. Userflow resolves a company against the CURRENTLY identified
				// user, so issuing this before the identify settles can attach the workspace to
				// whoever was identified previously, or to nobody on a first load.
				this.runGroup(props);
			})
			?.catch((error) => {
				// eslint-disable-next-line no-console
				console.error('Error identifying user', error);
			})
			// Either way the queue is decided here: on success it flushes, on failure it drains
			// rather than growing for the rest of the page.
			?.finally(() => {
				if (this.hasIdentified) this.flushTrackQueue();
				else this.trackQueue = [];
			});
	}

	private flushPendingIdentify(): void {
		if (!this.pendingIdentify) return;
		const props = this.pendingIdentify;
		this.pendingIdentify = null;
		this.runIdentify(props);
	}

	identify(props: AnalyticsIdentity): void {
		if (!this.isAvailable || !props.userId) return;

		if (!this.hasInitialized) {
			this.pendingIdentify = props;
			return;
		}

		this.runIdentify(props);
	}

	initialize(): void {
		// Skip userflow under Playwright. The window marker is set by
		if (typeof window !== 'undefined' && window.__PLAYWRIGHT_TEST__) {
			this.isAvailable = false;
			return;
		}

		if (this.hasInitialized) return;

		if (!userflow?.init) {
			if (this._initAttempts >= MAX_INIT_ATTEMPTS) {
				this.disableAfterLoadFailure(
					new Error('Userflow.js could not be loaded; userflow analytics disabled.')
				);
				return;
			}
			this._initAttempts++;
			setTimeout(() => {
				this.initialize();
			}, 200 * this._initAttempts);
			return;
		}
		try {
			userflow.init(this.config.userflowToken);
			this.hasInitialized = true;
			this.flushPendingIdentify();
		} catch (error) {
			this.disableAfterLoadFailure(error);
		}
	}

	page(): void {
		// userflow?.page();
	}

	transformPageAttributes(props: Properties): EventAttributes {
		return Object.entries(props).reduce((previous, [key, value]) => {
			previous[key] = value as EventAttributes[string];
			return previous;
		}, {} as EventAttributes);
	}

	track(event: string, source?: string, props?: Properties): void {
		if (!this.isAvailable) return;

		const baseProps = props ?? {};
		const propsWithSource = source ? { ...baseProps, source } : baseProps;
		if (!this.hasIdentified || !this.hasInitialized) {
			// Bounded: before identify resolves there is nothing to drain the queue, and a blocked
			// CDN script means it never resolves at all.
			if (this.trackQueue.length >= MAX_QUEUED_TRACKS) this.trackQueue.shift();
			this.trackQueue.push({ event, props: propsWithSource });
			return;
		}
		void userflow?.track(event, this.transformPageAttributes(propsWithSource))?.catch((error) => {
			// eslint-disable-next-line no-console
			console.error('Error tracking event', error);
		});
	}
}
